/**
 * Línea de tiempo de la cuenta.
 *
 * Muestra **solo los hitos que existen en el contrato**: el estado actual (con su
 * antigüedad, que es lo que permite fechar el cambio) y el último contacto
 * registrado. El panel todavía no guarda el historial de transiciones, así que la
 * línea lo dice en pantalla en vez de dibujar estados anteriores inventados —
 * un timeline verosímil y falso es peor que uno corto y cierto.
 */
import {
  CLIENT_STATE_LABELS,
  formatRelativeDays,
  type Client,
  type ClientLinks,
} from '@ops/contracts'

import { formatFechaCorta } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import {
  ExternalLink,
  ICONO_SEVERIDAD,
  Icon,
  SEVERIDAD_TEXTO,
  SEVERIDAD_TONO,
  Stack,
} from '../../ui'
import {
  DIAS_SIN_CONTACTO_ALERTA,
  diasDesde,
  isoDesdeDias,
  saludEstado,
} from './derivados'
import type { Health } from '@ops/contracts'

export interface TimelineEstadosProps {
  cliente: Client
  ahora: Date
  links: ClientLinks | undefined
}

interface Hito {
  clave: string
  titulo: string
  detalle: string
  iso: string | null
  dias: number | null
  severidad: Health
}

/** Sin contacto registrado no es "al día" ni "vencido": es sin dato. */
function severidadContacto(dias: number | null): Health {
  if (dias === null) return 'unknown'
  return dias > DIAS_SIN_CONTACTO_ALERTA ? 'warning' : 'ok'
}

export function TimelineEstados({ cliente, ahora, links }: TimelineEstadosProps) {
  const vincular = useVincular()
  const diasContacto = diasDesde(cliente.ultimoContacto, ahora)

  const hitos: Hito[] = [
    {
      clave: 'estado',
      titulo: `Pasó a ${CLIENT_STATE_LABELS[cliente.estado]}`,
      detalle: `lleva ${cliente.diasEnEstado} días en este estado`,
      iso: isoDesdeDias(cliente.diasEnEstado, ahora),
      dias: cliente.diasEnEstado,
      severidad: saludEstado(cliente.estado),
    },
    {
      clave: 'contacto',
      titulo:
        cliente.ultimoContacto === null
          ? 'Sin contacto registrado'
          : 'Último contacto registrado',
      detalle:
        cliente.ultimoContacto === null
          ? 'nadie cargó una fecha de contacto para esta cuenta'
          : formatRelativeDays(cliente.ultimoContacto, ahora),
      iso: cliente.ultimoContacto,
      dias: diasContacto,
      severidad: severidadContacto(diasContacto),
    },
  ].sort((a, b) => (a.dias ?? Number.MAX_SAFE_INTEGER) - (b.dias ?? Number.MAX_SAFE_INTEGER))

  return (
    <Stack gap="sm">
      <ol className="flex list-none flex-col gap-3 p-0">
        {hitos.map((hito) => (
          <li key={hito.clave} className="grid grid-cols-[18px_1fr] gap-2">
            <span
              aria-hidden="true"
              className={`grid h-[18px] w-[18px] place-items-center rounded-full border ${SEVERIDAD_TONO[hito.severidad]} ${SEVERIDAD_TEXTO[hito.severidad]}`}
            >
              <Icon name={ICONO_SEVERIDAD[hito.severidad]} size={10} />
            </span>
            <span className="min-w-0">
              <span className="block text-meta text-text">{hito.titulo}</span>
              <span className="block text-micro text-muted-strong">
                {hito.iso === null ? 'sin fecha' : formatFechaCorta(hito.iso)} ·{' '}
                {hito.detalle}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <p className="text-micro text-muted-strong">
        El panel guarda el estado actual y hace cuánto cambió, no el historial
        completo de transiciones. Los cambios anteriores viven en el lead.{' '}
      </p>
      <ExternalLink
        link={links?.close ?? null}
        sistemaFaltante="Close"
        onVincular={() => vincular(`el lead en Close de ${cliente.nombre}`)}
        textoVincular="Pedir que se vincule"
      >
        Ver la actividad completa en Close
      </ExternalLink>
    </Stack>
  )
}
